import { AssetAmount, AssetRatio, IAssetAmountMetadata } from "@sundaeswap/asset";
import isNaN from "lodash/isNaN";
import sortBy from "lodash/sortBy";

import { TasteTest, TokenStats } from "../gql/generated/taste-test.sdk";
import { ADA_METADATA } from "./../constants/cardano.constants";
import { formatCurrencyToAda } from "./number-format";

/**
 * Calculate the ADA equivalent for the gathered asset.
 *
 * @param currentBalance The current balance data for the taste test,
 * @returns ADA equivalent for a unit of the gathered asset.
 */
export const calculateTargetPrice = (
  currentBalance?: TokenStats | undefined,
  currentLocale?: string
) => {
  if (!currentBalance) {
    return {
      display: "0",
      raw: 0,
    };
  }

  const tokenSuppliedAmount = new AssetAmount(
    currentBalance.tokenSupplied.amount || 0,
    currentBalance.tokenSupplied.asset
  );
  const tokenGatheredAmount = new AssetAmount(
    currentBalance.tokenGathered.amount || 0,
    currentBalance.tokenGathered.asset
  );
  const ratio = new AssetRatio(tokenGatheredAmount, tokenSuppliedAmount);
  const targetPrice = ratio.getLimitPrice();

  if (isNaN(targetPrice)) {
    return {
      display: "0",
      raw: 0,
    };
  }

  return {
    display: formatCurrencyToAda(targetPrice, currentLocale),
    raw: targetPrice,
  };
};

/**
 * Get the current balance amounts for the gathered and supplied assets.
 */
export const getCurrentBalanceAmounts = (currentBalance: TokenStats | undefined) => {
  if (!currentBalance) {
    return {
      gatheredAssetAmount: new AssetAmount<IAssetAmountMetadata>(0n, ADA_METADATA),
      suppliedAssetAmount: new AssetAmount<IAssetAmountMetadata>(0n, ADA_METADATA),
    };
  }

  const gatheredAssetAmount = new AssetAmount<IAssetAmountMetadata>(
    currentBalance.tokenGathered.amount || 0,
    ADA_METADATA
  );
  const suppliedAssetAmount = new AssetAmount<IAssetAmountMetadata>(
    currentBalance.tokenSupplied.amount || 0,
    currentBalance.tokenSupplied.asset
  );

  return {
    gatheredAssetAmount,
    suppliedAssetAmount,
  };
};

/**
 * A constant URL string to initiate a mailto: request for launching a Taste Test with the us.
 * @type {string}
 */
export const createTasteTestUrl = `mailto:contact@sundaeswap.finance?subject=Launch%20a%20Taste%20Test&body=Hi%20Sundae%20Swap%20Team%2C%0A%0AI%20would%20like%20to%20launch%20a%20Taste%20Test%20for%20my%20project.%0A%0AHere%20are%20the%20details%3A%0A%0A%0A%0A%0A%0A%0A%0A%0A`;

/**
 * Sorts an array of TasteTest objects in ascending order based on their creation date.
 *
 * @param {TasteTest[]} tasteTests - The array of TasteTest objects to sort.
 * @returns {TasteTest[]} - The sorted array of TasteTest objects.
 */
export const sortTasteTestsByCreatedDate = (tasteTests: TasteTest[] | undefined) =>
  sortBy(tasteTests ?? [], (tasteTest) => tasteTest.createdDate.format);

/**
 * Determines whether a given URL represents an image, a video, or is of an unknown type based on its file extension.
 *
 * This function splits the URL string to extract its file extension, then checks whether this extension matches
 * a list of known image or video file extensions. It's a simple way to guess the content type of a file based on its URL.
 * However, it relies on the URL containing a file extension which matches the actual content type, which may not always be the case.
 * For more accurate content type detection, consider using server-side checks or examining the Content-Type HTTP header of a resource.
 *
 * @param {string} url - The URL of the file to check. This should be a well-formed URL string that includes a file name and extension.
 * @returns {'image' | 'video' | 'unknown'} - Returns 'image' if the URL's file extension is recognized as an image file,
 * 'video' if recognized as a video file, or 'unknown' if the extension does not match any known types or if the URL does not include an extension.
 */
export const getFileType = (url?: string): "image" | "video" | "unknown" => {
  if (!url) return "unknown"; // URL is empty or undefined

  // Lists of common image and video file extensions
  const imageExtensions = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp"];
  const videoExtensions = ["mp4", "avi", "mov", "wmv", "flv", "mkv"];

  // Extract the extension from the URL
  const extension = url.split(".").pop()?.toLowerCase();

  if (!extension) {
    return "unknown"; // No extension found
  }

  if (imageExtensions.includes(extension)) {
    return "image";
  } else if (videoExtensions.includes(extension)) {
    return "video";
  } else {
    return "unknown"; // Extension is not recognized as image or video
  }
};
